import { FC, useCallback, useMemo, useState } from 'react';
import { useStore } from '@nanostores/react';
import styled from 'styled-components';
import {
  $selectedPreviewEvent,
  $selectedEvent,
  $plannerModalIsOpen,
  $selectedDetailsEvent,
  removeEvent,
} from '../store/planner';
import { Modal } from 'antd';
import dayjs from 'dayjs';
import { lighterBlueBackground } from '../components/colors';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { StyledRadioButton } from './PlanFormPage.style';
import { EventType, eventTypeOptions } from './PlanFormPage';

const ModalBody = styled.div<{ $hasId: boolean }>`
  display: flex;
  flex-direction: ${({ $hasId }) => ($hasId ? 'row' : 'column')};
  align-items: ${({ $hasId }) => ($hasId ? 'stretch' : 'center')};
  gap: 24px;
  padding-top: 15px;
`;

const LeftColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const RightColumn = styled.div<{ $centered: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ $centered }) =>
    $centered
      ? `
    align-items: center;
    width: 100%;
  `
      : `
    align-items: flex-end;
  `}
`;

const EventTitle = styled.h3`
  margin: 0 0 4px 0;
  padding: 0;
`;

const EventDescription = styled.div`
  margin: 0;
  color: rgba(0, 0, 0, 0.65);
`;

const DateTimeRow = styled.div`
  display: flex;
  gap: 24px;
`;

const DateTimeBlock = styled.div`
  text-align: right;
`;

const TimeLabel = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
`;

const TimeValue = styled.h3`
  margin: 0;
  padding: 0;
`;

const DateValue = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const TypeColorBar = styled.div<{ $color?: string }>`
  position: absolute;
  left: 0;
  top: 60px;
  height: 3px;
  width: 100%;
  background-color: ${({ $color }) => $color || '#fff'};
`;

const CreateEventSection = styled.div`
  background: ${lighterBlueBackground};
  border-radius: 10px;
  margin-top: 20px;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

export const PlannerEventPreviewModal: FC = () => {
  const previewEvent = useStore($selectedPreviewEvent);
  const [eventType, setEventType] = useState<EventType>('training');

  const handleOpenDetails = () => {
    if (previewEvent) {
      $selectedDetailsEvent.set(previewEvent);
      $selectedPreviewEvent.set(null);
    }
  };

  const handleEdit = () => {
    if (previewEvent) {
      $selectedEvent.set(previewEvent);
      $selectedPreviewEvent.set(null);
      setTimeout(() => {
        $plannerModalIsOpen.set(true);
      }, 200);
    }
  };

  const handleDelete = async () => {
    if (previewEvent && previewEvent.id) {
      await removeEvent(previewEvent.id as string);
      $selectedPreviewEvent.set(null);
    }
  };

  const title = useMemo(() => {
    if (!previewEvent || !previewEvent.id) return 'Create New Event';
    if (previewEvent.planType == 'training') {
      return 'Training Event';
    }
    if (previewEvent.planType == 'match') {
      return 'Match Event';
    }
    if (previewEvent.planType == 'other') {
      return 'Other Event';
    }
  }, [previewEvent]);

  const typeColor = useMemo(() => {
    if (!previewEvent) return '#fff';
    if (previewEvent.planType == 'training') {
      return '#34ac0c';
    }
    if (previewEvent.planType == 'match') {
      return '#dc3545';
    }
    if (previewEvent.planType == 'other') {
      return '#ffc107';
    }
  }, [previewEvent]);

  const createEvent = useCallback(() => {
    $selectedEvent.set({
      ...previewEvent!,
      planType: eventType,
    });
    $selectedPreviewEvent.set(null);
    setTimeout(() => {
      $plannerModalIsOpen.set(true);
    }, 200);
  }, [eventType, previewEvent]);

  const hasId = !!previewEvent?.id;

  return (
    <Modal
      title={title}
      open={previewEvent != null}
      closable={true}
      maskClosable={false}
      onClose={() => $selectedPreviewEvent.set(null)}
      onCancel={() => $selectedPreviewEvent.set(null)}
      footer={null}
      closeIcon={
        <div style={{ color: 'black' }}>
          <XMarkIcon style={{ width: '34px' }} />
        </div>
      }
    >
      {previewEvent && (
        <>
          <TypeColorBar $color={typeColor} />

          <ModalBody $hasId={hasId}>
            {hasId && (
              <LeftColumn>
                <EventTitle>{previewEvent.title}</EventTitle>
                <EventDescription>{previewEvent.description}</EventDescription>
              </LeftColumn>
            )}

            <RightColumn $centered={!hasId}>
              <DateTimeRow>
                {previewEvent.start != undefined && (
                  <DateTimeBlock>
                    <TimeLabel>Begin</TimeLabel>
                    <TimeValue>
                      {dayjs(previewEvent.start.toString()).format('HH:mm')}
                    </TimeValue>
                    <DateValue>
                      {dayjs(previewEvent.start.toString()).format(
                        'YYYY-MM-DD',
                      )}
                    </DateValue>
                  </DateTimeBlock>
                )}
                {previewEvent.end != undefined && (
                  <DateTimeBlock>
                    <TimeLabel>End</TimeLabel>
                    <TimeValue>
                      {dayjs(previewEvent.end.toString()).format('HH:mm')}
                    </TimeValue>
                    <DateValue>
                      {dayjs(previewEvent.end.toString()).format('YYYY-MM-DD')}
                    </DateValue>
                  </DateTimeBlock>
                )}
              </DateTimeRow>

              {hasId && (
                <ActionButtons>
                  <Button
                    large={false}
                    tertiary={true}
                    onClick={handleOpenDetails}
                  >
                    Details
                  </Button>
                  <Button large={false} tertiary={true} onClick={handleEdit}>
                    Edit
                  </Button>
                  <Button
                    large={false}
                    tertiary={true}
                    onClick={handleDelete}
                    style={{ backgroundColor: '#dc3545', color: 'white' }}
                  >
                    Delete
                  </Button>
                </ActionButtons>
              )}
            </RightColumn>
          </ModalBody>

          {!hasId && (
            <CreateEventSection>
              <h3 style={{ color: 'white' }}>Create Event</h3>
              <StyledRadioButton
                options={eventTypeOptions}
                highlightSelect={true}
                selected={eventType}
                onSelect={(value) => setEventType(value as EventType)}
                $eventType={eventType}
                $selectedValue={eventType}
              />
              <Button
                style={{ marginTop: '20px', height: '60px' }}
                large={false}
                tertiary={true}
                onClick={createEvent}
              >
                Create{' '}
                {eventTypeOptions.find((option) => option.id === eventType)
                  ?.main
                  ? eventType == 'other'
                    ? 'note'
                    : eventType
                  : ''}
              </Button>
            </CreateEventSection>
          )}
        </>
      )}
    </Modal>
  );
};
